import React, { useState } from "react";
import { QRCode } from "../types";
import {
  downloadQRCodePNG,
  downloadQRCodeSVG,
  generateQRCodeURL,
} from "../utils/qrGenerator";
import QRCodeDisplay from "./QRCodeDisplay";

interface QRCodeListProps {
  qrCodes: QRCode[];
  onEdit: (qrCode: QRCode) => void;
  onOpen?: (qrCode: QRCode) => void;
  onDuplicate?: (qrCode: QRCode) => void;
  onToggleFavorite?: (qrCode: QRCode) => void;
  onToggleArchive?: (qrCode: QRCode) => void;
  onDelete: (qrCode: QRCode) => void;
  onCreate?: () => void;
  loading?: boolean;
  compact?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
}

const QRCodeList: React.FC<QRCodeListProps> = ({
  qrCodes,
  onEdit,
  onCreate,
  onOpen,
  onDuplicate,
  onToggleFavorite,
  onToggleArchive,
  loading = false,
  compact = false,
  selectedIds = [],
  onSelectionChange,
}) => {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyDynamicLink = async (qrCode: QRCode) => {
    try {
      await navigator.clipboard.writeText(generateQRCodeURL(qrCode.id));
      setCopiedId(qrCode.id);
      window.setTimeout(() => setCopiedId(null), 1800);
    } catch (copyError) {
      console.error("Failed to copy:", copyError);
    }
  };

  const formatDate = (date?: Date) => {
    if (!date) return "Noch nie";
    return new Intl.DateTimeFormat("de-DE", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  if (loading) {
    return (
      <div className="divide-y divide-slate-100">
        {Array.from({ length: compact ? 3 : 5 }).map((_, index) => (
          <div
            key={index}
            className="flex animate-pulse items-center gap-4 px-5 py-5 sm:px-6"
          >
            <div className="h-16 w-16 rounded-xl bg-slate-100" />
            <div className="flex-1">
              <div className="h-4 w-40 rounded bg-slate-100" />
              <div className="mt-3 h-3 w-64 max-w-full rounded bg-slate-100" />
            </div>
            <div className="hidden h-8 w-24 rounded bg-slate-100 sm:block" />
          </div>
        ))}
      </div>
    );
  }

  if (!qrCodes.length) {
    return (
      <div className="px-5 py-16 text-center sm:px-6">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-400">
          <svg
            className="h-6 w-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          >
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <path d="M14 14h3v3h-3zM19 14h2v2M19 19h2v2M14 19h3v2" />
          </svg>
        </div>
        <h3 className="mt-4 text-base font-semibold text-slate-900">
          Keine QR-Codes gefunden
        </h3>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-500">
          Erstelle deinen ersten dynamischen QR-Code oder setze die Filter
          zurück.
        </p>
        {onCreate && (
          <button
            type="button"
            onClick={onCreate}
            className="app-primary-button mx-auto mt-5"
          >
            <span className="text-lg leading-none">+</span>QR-Code erstellen
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100">
      {qrCodes.map((qrCode) => (
        <article
          key={qrCode.id}
          className="group relative px-4 py-4 transition-colors hover:bg-slate-50/80 sm:px-6"
        >
          <div className="flex items-start gap-4">
            {onSelectionChange && (
              <label className="mt-6 flex-shrink-0">
                <span className="sr-only">
                  {qrCode.title || qrCode.id} auswählen
                </span>
                <input
                  type="checkbox"
                  checked={selectedIds.includes(qrCode.id)}
                  onChange={(event) =>
                    onSelectionChange(
                      event.target.checked
                        ? [...selectedIds, qrCode.id]
                        : selectedIds.filter((id) => id !== qrCode.id),
                    )
                  }
                  className="h-4 w-4 rounded border-slate-300 text-blue-600"
                />
              </label>
            )}
            <div className="hidden h-[76px] w-[76px] flex-shrink-0 place-items-center overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-sm sm:grid">
              <QRCodeDisplay
                qrId={qrCode.id}
                size={66}
                showDownloadButton={false}
                styleOptions={qrCode.styleOptions}
              />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onOpen?.(qrCode)}
                      className="truncate text-left text-sm font-semibold text-slate-950 hover:text-blue-600"
                    >
                      {qrCode.title || qrCode.id}
                    </button>
                    <HealthBadge qrCode={qrCode} />
                    {qrCode.favorite && (
                      <span title="Favorit" className="text-amber-500">
                        ★
                      </span>
                    )}
                  </div>
                  <p
                    className="mt-1 truncate text-sm text-slate-500"
                    title={qrCode.target}
                  >
                    {qrCode.target}
                  </p>
                  {!compact && qrCode.description && (
                    <p className="mt-2 line-clamp-2 text-xs leading-5 text-slate-400">
                      {qrCode.description}
                    </p>
                  )}
                </div>

                <dl className="grid grid-cols-3 gap-2 xl:w-[330px] xl:flex-shrink-0">
                  <Stat
                    label="Scans"
                    value={(qrCode.accessCount || 0).toLocaleString()}
                  />
                  <Stat
                    label="Letzter Scan"
                    value={formatDate(qrCode.lastAccessed)}
                  />
                  <Stat label="Erstellt" value={formatDate(qrCode.createdAt)} />
                </dl>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2 xl:absolute xl:right-6 xl:top-1/2 xl:mt-0 xl:-translate-y-1/2 xl:translate-x-3 xl:rounded-xl xl:border xl:border-slate-200 xl:bg-white xl:p-1.5 xl:opacity-0 xl:shadow-lg xl:transition-all xl:group-hover:translate-x-0 xl:group-hover:opacity-100 xl:group-focus-within:translate-x-0 xl:group-focus-within:opacity-100">
                <ActionButton label="Bearbeiten" onClick={() => onEdit(qrCode)}>
                  <path d="m4 16-.8 4 4-.8L18.5 7.9a2 2 0 0 0-2.8-2.8L4 16zM14.5 6.5l3 3" />
                </ActionButton>
                {onOpen && (
                  <ActionButton
                    label="Analytics"
                    onClick={() => onOpen(qrCode)}
                  >
                    <path d="M4 19V9m5 10V5m5 14v-7m5 7V3" />
                  </ActionButton>
                )}
                {onToggleFavorite && (
                  <ActionButton
                    label={qrCode.favorite ? "Favorit entfernen" : "Favorit"}
                    onClick={() => onToggleFavorite(qrCode)}
                  >
                    <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.6l6.2-.9L12 3z" />
                  </ActionButton>
                )}
                {onDuplicate && (
                  <ActionButton
                    label="Duplizieren"
                    onClick={() => onDuplicate(qrCode)}
                  >
                    <rect x="8" y="8" width="11" height="11" rx="2" />
                    <path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" />
                  </ActionButton>
                )}
                {onToggleArchive && (
                  <ActionButton
                    label={
                      qrCode.status === "archived"
                        ? "Aktivieren"
                        : "Archivieren"
                    }
                    onClick={() => onToggleArchive(qrCode)}
                  >
                    <path d="M4 7h16v13H4zM3 4h18v3H3zM9 11h6" />
                  </ActionButton>
                )}
                <ActionButton
                  label={copiedId === qrCode.id ? "Kopiert" : "Link kopieren"}
                  onClick={() => void copyDynamicLink(qrCode)}
                >
                  <rect x="8" y="8" width="11" height="11" rx="2" />
                  <path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" />
                </ActionButton>
                <ActionButton
                  label="PNG"
                  onClick={() =>
                    void downloadQRCodePNG(
                      qrCode.id,
                      `${qrCode.title || qrCode.id}.png`,
                      qrCode.styleOptions,
                    )
                  }
                >
                  <path d="M12 3v12m0 0 4-4m-4 4-4-4M5 20h14" />
                </ActionButton>
                <ActionButton
                  label="SVG"
                  onClick={() =>
                    void downloadQRCodeSVG(
                      qrCode.id,
                      `${qrCode.title || qrCode.id}.svg`,
                      qrCode.styleOptions,
                    )
                  }
                >
                  <path d="M8 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9l-5-5h-3M14 4v5h5M8 13l-2 2 2 2M16 13l2 2-2 2M13 12l-2 6" />
                </ActionButton>
              </div>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="min-w-0">
    <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
      {label}
    </dt>
    <dd
      className="mt-1 truncate text-xs font-semibold text-slate-700"
      title={value}
    >
      {value}
    </dd>
  </div>
);

const ActionButton: React.FC<{
  label: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}> = ({ label, onClick, danger, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold transition ${danger ? "text-red-600 hover:bg-red-50" : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"}`}
  >
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
    <span className="xl:sr-only">{label}</span>
  </button>
);

export default QRCodeList;

const HealthBadge: React.FC<{ qrCode: QRCode }> = ({ qrCode }) => {
  const status =
    qrCode.status === "archived"
      ? "archived"
      : qrCode.health?.status || (qrCode.accessCount ? "active" : "unscanned");
  const meta: Record<string, [string, string]> = {
    active: ["Aktiv", "bg-emerald-50 text-emerald-700"],
    unscanned: ["Ungescannt", "bg-slate-100 text-slate-600"],
    declining: ["Sinkend", "bg-amber-50 text-amber-700"],
    unreachable: ["Nicht erreichbar", "bg-red-50 text-red-700"],
    slow: ["Langsam", "bg-orange-50 text-orange-700"],
    inactive: ["Inaktiv", "bg-slate-200 text-slate-600"],
    archived: ["Archiviert", "bg-slate-200 text-slate-600"],
    checking: ["Prüfung", "bg-blue-50 text-blue-700"],
  };
  const [label, classes] = meta[status] || meta.checking;
  return (
    <span
      className={`inline-flex flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${classes}`}
    >
      {label}
    </span>
  );
};
